import { useState } from 'react';
import type { DocumentRenderSection } from '@vivport/backend/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { assertToolSucceeded } from '@/api/templates-api';
import { useSetFieldValue } from '@/hooks/useTemplateQueries';
import { EMPTY_CONTROL_STATE, resolveControlValue, type FieldControlState } from './field-control-state';
import { TemplateFieldControl } from './TemplateFieldControl';
import { toolErrorText } from '@/utils/tool-error';
import { runCaptureWrite } from '@/utils/mutation-retry';
import { InlineRetryError } from '@/components/ui/inline-retry-error';
import { Loader2 } from 'lucide-react';

interface TemplateFillPanelProps {
  section: DocumentRenderSection;
  instanceRootId: number;
}

interface RowState extends FieldControlState {
  error: string | null;
}

// Which fields are unfilled is a render fact (templateSection.fields[].filled); the panel only
// draws a control per unfilled member-facing field and hands the value to templates.setFieldValue.
export function TemplateFillPanel({ section, instanceRootId }: TemplateFillPanelProps) {
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const setFieldValue = useSetFieldValue();
  const facts = section.templateSection;
  if (facts === null) return null;
  const unfilled = facts.fields.filter(
    (field) => !field.filled && field.fillBy !== 'writer' && field.placement.kind !== 'column'
  );
  if (unfilled.length === 0) return null;

  const rowFor = (key: string): RowState => rows[key] ?? { ...EMPTY_CONTROL_STATE, error: null };
  const updateRow = (key: string, patch: Partial<RowState>) =>
    setRows((prev) => ({ ...prev, [key]: { ...rowFor(key), ...prev[key], ...patch } }));

  const save = async (fieldKey: string) => {
    const value = resolveControlValue(rowFor(fieldKey));
    if (value.length === 0) return;
    setSavingKey(fieldKey);
    updateRow(fieldKey, { error: null });
    try {
      await runCaptureWrite({
        label: 'templates.setFieldValue',
        write: async () =>
          assertToolSucceeded(
            await setFieldValue.mutateAsync({
              instanceId: instanceRootId,
              sectionCode: section.code,
              fieldKey,
              value,
            })
          ),
      });
      setRows((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
    } catch (error) {
      updateRow(fieldKey, { error: toolErrorText(error) });
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div data-testid="template-fill-panel" className="mt-3 space-y-3 rounded-md border bg-muted/30 p-3">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Details</p>
      {unfilled.map((field) => {
        const row = rowFor(field.key);
        const controlId = `fill-${section.id}-${field.key}`;
        const saving = savingKey === field.key;
        const canSave = resolveControlValue(row).length > 0 && savingKey === null;
        return (
          <div key={field.key} className="grid gap-1.5 sm:grid-cols-[minmax(8rem,12rem)_1fr_auto] sm:items-start">
            <Label htmlFor={controlId} className="flex items-center gap-1.5 pt-1.5 text-sm">
              {field.label}
              {field.required === true ? <Badge variant="outline">required</Badge> : null}
            </Label>
            <div className="space-y-1">
              <TemplateFieldControl
                id={controlId}
                spec={field}
                state={row}
                onChange={(state) => updateRow(field.key, { ...state, error: null })}
                disabled={savingKey !== null}
              />
              {row.error !== null ? (
                <InlineRetryError
                  message={row.error}
                  onRetry={() => void save(field.key)}
                  disabled={savingKey !== null}
                />
              ) : null}
            </div>
            <Button size="sm" className="max-sm:h-11 max-sm:w-full" onClick={() => void save(field.key)} disabled={!canSave}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}Save
            </Button>
          </div>
        );
      })}
    </div>
  );
}
